import {
	useMemo, useState, useCallback, useContext,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GRS } from '../stores/types/types';
import Socket, { ConnectedUser } from '../connection/socket';
import { BACKSTAGE_COMMS_KEY_STATES } from '../stores/reducers/session';
import * as SessionActions from '../stores/actions/session';
import { VideoStream } from '../components/types/videos';
import { getNameOrInitials } from '../utils/utils';
import * as VideoActions from '../stores/actions/video';
import { Invitation } from '../stores/reducers/invitations';
import { setVideoTitlesHaveFired } from '../stores/actions/shots';
import { getUserWebRTCIdMap } from '../utils/user';
import { AVContext, AVDataInterface } from '../contexts/audio-video';
import { OpenTokContext } from '../contexts/opentok/opentok-context';
import { deleteCommToggle, setCommToggle } from '../connection/comm-toggle';
import useShots from '../contexts/shots/provider';

const useUserActions = () => {
	const dispatch = useDispatch();
	const backstageCommsKeyState = useSelector((s: GRS) => s.SessionReducer.backstageCommsKeyState);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const session = useSelector((s: GRS) => s.SessionReducer.session);
	const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const hiddenTitles = useSelector((s: GRS) => s.VideoReducer.hiddenTitles);
	const sharingScreen = useSelector((s: GRS) => s.VideoReducer.sharingScreen);
	const media = useSelector((s: GRS) => s.MediaReducer.media);
	const invitations = useSelector((s: GRS) => s.InvitationsReducer.invitations);
	const videoTitlesHaveFired = useSelector((s: GRS) => s.ShotsReducer.videoTitlesHaveFired);
	const { titlesDuration } = useSelector((s: GRS) => s.ShotsReducer.canvasStyle);

	const [socket, setSocket] = useState<Socket | null>(null);

	const { getAV, getAllAV } = useContext(AVContext);
	const { opentok } = useContext(OpenTokContext);
	const { isOnCanvas, currentShot } = useShots();

	const connectedUserMap = useMemo(
		() => getUserWebRTCIdMap(connectedUsers),
		[connectedUsers, webrtcID],
	);

	const idsOfVideosInCanvas = useMemo(() => {
		const newIdsOfVideosInCanvas = [] as string[];
		currentShot.shot_data.shotFeeds.forEach((feed) => {
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) newIdsOfVideosInCanvas.push(connectedVideoForMedia.id);
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				} else {
					const restrictedVideoForPresenter = connectedUsers.find(
						(u: ConnectedUser) => u.gr_user === feed.presenter && !u.watcher_only,
					);
					if (restrictedVideoForPresenter?.socketId) newIdsOfVideosInCanvas.push(`${restrictedVideoForPresenter.socketId}-restricted-user`);
				}
			}
		});
		return newIdsOfVideosInCanvas;
	}, [connectedPresenterVideos, connectedMediaVideos, connectedUsers, currentShot]);

	const backstageCommsToggle = useCallback((muted: boolean, targetUser: string = '') => {
		// if tagetUser is empty, then we are toggling all comms

		if (
			backstageCommsKeyState === BACKSTAGE_COMMS_KEY_STATES.ALL
			|| backstageCommsKeyState === BACKSTAGE_COMMS_KEY_STATES.INDIVIDUAL
			|| isOnCanvas
		) return;

		if (!meeting?.meeting || !user?.gr_user || !token) {
			return;
		}

		dispatch(SessionActions.setBackstageCommsTargetWebRtcId(muted ? '' : targetUser));

		if (muted) {
			dispatch(SessionActions.setBackstageCommsKeyState(BACKSTAGE_COMMS_KEY_STATES.CLICK));
			deleteCommToggle(meeting.meeting, token, user.gr_user);
		} else {
			dispatch(SessionActions.setBackstageCommsKeyState(BACKSTAGE_COMMS_KEY_STATES.OFF));
			setCommToggle(meeting.meeting, token, user.gr_user, targetUser, !targetUser);
		}

		const targetedPresenterID = targetUser || null;
		socket?.sendMessage('backstage-comms-toggle', { muted, producerID: webrtcID, targetedPresenterID });
	}, [meeting, user, token, backstageCommsKeyState, socket, webrtcID]);

	const handleResetTitles = () => {
		if (!hiddenTitles?.length) {
			const connectedUserIdArray = connectedUsers.map((u) => u.gr_user);

			dispatch(VideoActions.setHiddenTitles(connectedUserIdArray));
			socket?.sendMessage(
				'toggle-all-titles',
				[
					connectedUserIdArray,
					currentShot.shot_id,
					session,
				],
			);
			return;
		}

		// only re-fire titles if they have a 5 or 10 second duration
		if (!titlesDuration) return;

		if (videoTitlesHaveFired) {
			dispatch(setVideoTitlesHaveFired(false));
		}

		dispatch(VideoActions.setHiddenTitles([0]));
		socket?.sendMessage('toggle-all-titles', [[0], currentShot.shot_id, session]);
	};

	const handleToggleTitle = (video: VideoStream) => {
		if (connectedUserMap.has(video.id)) {
			const profile = connectedUserMap.get(video.id)?.gr_user;

			if (!profile) {
				return;
			}

			dispatch(VideoActions.toggleTitle(profile));
			socket?.sendMessage('toggle-title', { from: session, profile });
		}
	};

	const iOSMuteMessage = useCallback((
		id: string,
		messageType: 'mute-audio' | 'mute-video',
		muted: boolean, // if true, is muted,
	) => {
		const connectedUser = connectedUserMap.get(id);
		if (connectedUser?.socketId) {
			socket?.sendMessage(messageType, muted, connectedUser.socketId);
		}
	}, [connectedUserMap, socket]);

	const toggleAudio = useCallback((webRTCId: string) => {
		opentok?.forceSetAudio(webRTCId, !getAV(webRTCId).audio);
		iOSMuteMessage(webRTCId, 'mute-audio', getAV(webRTCId)?.audio ?? true);
	}, [getAV, iOSMuteMessage]);

	const toggleVideo = useCallback((webRTCId: string) => {
		opentok?.forceSetVideo(webRTCId, !getAV(webRTCId).video);
		iOSMuteMessage(webRTCId, 'mute-video', getAV(webRTCId)?.video ?? true);
	}, [getAV, iOSMuteMessage]);

	const toggleMuteAll = useCallback((
		value: boolean,
	) => {
		const avData = getAllAV();
		const values = Object.values(avData).filter((av) => av.id !== 'local');

		values.forEach((data: AVDataInterface) => {
			opentok?.forceSetAudio(data.id, !value);
			iOSMuteMessage(data.id, 'mute-audio', true);
		});
	}, [getAllAV, connectedUserMap, opentok]);

	const getFeedName = useCallback((vid: VideoStream): string => {
		if (vid.isMedia) {
			const mediaObject = media.find((med) => med.id === vid.mediaId);
			return (mediaObject?.custom_name ? mediaObject.custom_name : mediaObject?.original_name) || '';
		} if (!vid.isScreenShare && vid.srcObject) {
			const userObject = connectedUsers.find(
				(connectedUser) => connectedUser.gr_user === vid.profile,
			);
			return userObject ? getNameOrInitials(userObject, true) : '';
		} if (vid.isScreenShare && vid.srcObject) {
			const userObject = connectedUsers.find(
				(connectedUser) => connectedUser?.feedWebRTCIds?.includes(vid.id),
			);
			return userObject ? `${getNameOrInitials(userObject, true)}'s Screen` : '';
		} if (vid.profile) {
			const invite = invitations.find(
				(i: Invitation) => vid.profile && i.profile.gr_user === vid.profile,
			);
			return invite ? getNameOrInitials(invite.profile, true) : '';
		} if (vid.email) {
			return vid.email;
		}
		return '';
	}, [media, connectedUsers, invitations]);

	const toggleScreenShareDialog = () => {
		dispatch(VideoActions.setSharingScreen(!sharingScreen));
	};

	return {
		backstageCommsToggle,
		handleToggleTitle,
		idsOfVideosInCanvas,
		toggleAudio,
		toggleVideo,
		toggleMuteAll,
		setSocket,
		handleResetTitles,
		getFeedName,
		toggleScreenShareDialog,
		iOSMuteMessage,
	};
};

export default useUserActions;
